import React, { useState, useRef, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  TextField,
  Button,
  Avatar,
  CircularProgress,
} from '@mui/material'
import {
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Send as SendIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { formatSalary, getPracticeTypeString, buildJobContextPrefix } from './helpers'
import { fetchDispatch } from 'data/helpers'
import TypingIndicator from './TypingIndicator'
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'

const getMatchTierColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent':
      return { bg: theme.palette.success.light, color: theme.palette.success.dark, border: theme.palette.success.main }
    case 'strong':
      return { bg: theme.palette.info.light, color: theme.palette.info.dark, border: theme.palette.info.main }
    case 'good':
      return { bg: theme.palette.warning.light, color: theme.palette.warning.dark, border: theme.palette.warning.main }
    case 'moderate':
    default:
      return { bg: theme.palette.grey[200], color: theme.palette.grey[700], border: theme.palette.grey[400] }
  }
}

const AIJobCardExpanded = ({
  rankedJob,
  onClose,
  onViewFullDetails,
  onFavoriteClick,
  isFavorite,
}) => {
  const theme = useTheme()
  const messagesEndRef = useRef(null)

  const [messages, setMessages] = useState([
    { type: 'ai', message: 'What would you like to know about this job?', isWelcome: true },
  ])
  const [conversationId, setConversationId] = useState(null)
  const [isTyping, setIsTyping] = useState(false)
  const [prompt, setPrompt] = useState('')
  const inputRef = useRef(null)

  const job = rankedJob?.job
  const matchTier = rankedJob?.matchTier
  const justification = rankedJob?.justification
  const matchTierColors = getMatchTierColor(matchTier, theme)
  const salaryDisplay = job ? formatSalary(job.salary || job.salaryMin || job.salaryMax) : null

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages, isTyping])

  if (!job) return null

  const handleSend = (e) => {
    e.preventDefault()
    if (!prompt.trim() || isTyping) return

    const userMessage = prompt.trim()
    setMessages(prev => [...prev, { type: 'user', message: userMessage }])
    setPrompt('')
    setIsTyping(true)

    const jobContextPrefix = !conversationId ? buildJobContextPrefix(job) : ''
    const apiContent = jobContextPrefix ? `${jobContextPrefix}\n\n${userMessage}` : userMessage

    fetchDispatch({
      path: '/api/job_chatbot/message',
      method: 'POST',
      values: { content: apiContent, conversationId },
    })
      .then(response => {
        setConversationId(response.conversationId)
        setMessages(prev => [...prev, { type: 'ai', message: response.content }])
        setIsTyping(false)
      })
      .catch(() => {
        setMessages(prev => [...prev, { type: 'ai', message: 'Sorry, something went wrong. Please try again.', isError: true }])
        setIsTyping(false)
      })
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        flexShrink: 0,
        borderRadius: 1,
        border: `2px solid ${theme.palette.ai.main}`,
        boxShadow: theme.palette.shadows.medium,
      }}
    >
      <CardContent sx={{ p: { xs: 1, md: 1.25 }, '&:last-child': { pb: { xs: 1, md: 1.25 } } }}>
        {/* Job Summary Header */}
        <Box sx={{ mb: 1 }}>
          {/* Row 1: Title + Practice Type + Favorite + Close */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography
              variant='subtitle1'
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 'medium',
                fontSize: '0.95rem',
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
              }}
            >
              {job.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
              {job.practiceType && getPracticeTypeString(job.practiceType) && (
                <Chip
                  label={getPracticeTypeString(job.practiceType)}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {matchTier && (
                <Chip
                  icon={<AutoAwesomeIcon sx={{ fontSize: '0.75rem !important' }} />}
                  label={matchTier}
                  size='small'
                  sx={{
                    height: '20px',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    backgroundColor: matchTierColors.border,
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              )}
              {onFavoriteClick && (
                <IconButton
                  aria-label='favorite'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    onFavoriteClick({ jobId: job.id, favorite: !isFavorite })
                  }}
                  sx={{ p: 0.25 }}
                >
                  {isFavorite
                    ? <FavoriteIcon color='error' sx={{ fontSize: '1rem' }} />
                    : <FavoriteBorderIcon color='error' sx={{ fontSize: '1rem' }} />
                  }
                </IconButton>
              )}
              <IconButton
                aria-label='close'
                size='small'
                onClick={(e) => { e.stopPropagation(); onClose() }}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>
          </Box>

          {/* Row 2: Employer · Location · Salary */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, color: theme.palette.text.secondary, mb: 0.5 }}>
            {job.employer?.name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BusinessIcon sx={{ fontSize: '0.85rem' }} color='action' />
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  {job.employer.name}
                </Typography>
              </Box>
            )}
            {(job.city || job.state) && (
              <>
                {job.employer?.name && (
                  <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>·</Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                    {job.city}{job.city && job.state && ', '}{job.state}
                  </Typography>
                </Box>
              </>
            )}
            {salaryDisplay && (
              <>
                <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>·</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachMoneyIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}>
                    {salaryDisplay}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Justification box */}
          {justification && (
            <Box
              sx={{
                backgroundColor: matchTierColors.bg,
                borderRadius: 1,
                p: 0.75,
                borderLeft: `3px solid ${matchTierColors.border}`,
                mb: 0.5,
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  fontSize: '0.78rem',
                  color: theme.palette.text.secondary,
                  lineHeight: 1.4,
                }}
              >
                {justification}
              </Typography>
            </Box>
          )}

          {/* View full details */}
          <Typography
            variant='caption'
            onClick={(e) => { e.stopPropagation(); onViewFullDetails() }}
            sx={{
              color: theme.palette.ai.main,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.75rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View full details
          </Typography>
        </Box>

        {/* Embedded Chat */}
        <Box
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Message list */}
          <Box
            sx={{
              height: 300,
              overflowY: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              backgroundColor: theme.palette.background.default,
            }}
          >
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  flexDirection: msg.type === 'user' ? 'row-reverse' : 'row',
                }}
              >
                {msg.type === 'user' ? (
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 28, height: 28 }}>
                    <PersonIcon sx={{ fontSize: '1.1rem' }} />
                  </Avatar>
                ) : (
                  <Avatar sx={{ backgroundColor: theme.palette.ai.main, width: 28, height: 28 }}>
                    <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.1rem' }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    backgroundColor: msg.isError
                      ? theme.palette.error.light
                      : (msg.type === 'user'
                        ? 'rgba(42, 181, 116, 0.15)'
                        : (msg.isWelcome ? `${theme.palette.ai.light}40` : theme.palette.grey[100])),
                    borderRadius: 2,
                    p: 1,
                    maxWidth: '85%',
                    border: msg.isError ? `1px solid ${theme.palette.error.main}` : 'none',
                    borderLeft: msg.isWelcome ? `3px solid ${theme.palette.ai.main}` : 'none',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap',
                      color: msg.isError ? theme.palette.error.dark : 'inherit',
                    }}
                  >
                    {msg.message}
                  </Typography>
                </Box>
              </Box>
            ))}
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </Box>

          {/* Chat input */}
          <Box
            component='form'
            onSubmit={handleSend}
            sx={{
              display: 'flex',
              gap: 1,
              p: 1,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              backgroundColor: theme.palette.background.paper,
              alignItems: 'flex-end',
            }}
          >
            <TextField
              fullWidth
              size='small'
              variant='outlined'
              placeholder='Ask about this job...'
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isTyping}
              inputRef={inputRef}
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem',
                  '&:hover fieldset': { borderColor: theme.palette.ai.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.ai.main },
                },
              }}
            />
            <Button
              type='submit'
              variant='contained'
              disabled={isTyping || !prompt.trim()}
              sx={{
                minWidth: '36px',
                height: '36px',
                p: 0,
                flexShrink: 0,
                backgroundColor: theme.palette.ai.main,
                '&:hover': { backgroundColor: theme.palette.ai.dark },
              }}
            >
              {isTyping
                ? <CircularProgress size={16} color='inherit' />
                : <SendIcon sx={{ fontSize: '1rem' }} />
              }
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default AIJobCardExpanded
